import { clsx } from "clsx"
import {
  type AriaRole,
  type ClipboardEventHandler,
  type CSSProperties,
  type FocusEvent,
  forwardRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react"

import type { SX, ThemeKeys } from "../../theme"
import { Tooltip } from "../Tooltip"
import styles from "./BaseInput.module.css"

/**
 * Flexible base Input, provided with no height or block padding.
 */

export interface BaseInputProps {
  autoComplete?: string
  id?: string
  inputMode?: "decimal" | "text"
  name?: string
  placeholder?: string
  readOnly?: boolean
  startAdornment?: ReactNode
  prefix?: ReactNode
  prefixTooltip?: string
  suffix?: ReactNode
  endAdornment?: ReactNode
  padding?: Padding
  value: string
  variant?: "normal" | "link" | "success" | "error"
  cursor?: "text" | "pointer" | "default"
  textOverflow?: "ellipsis"
  maxLength?: number
  disabled?: boolean
  type?: "text" | "email" | "password" | "number"
  sx?: SX
  onFocus?: (event: FocusEvent<HTMLInputElement, Element>) => void
  onBlur?: (event: FocusEvent<HTMLInputElement, Element>) => void
  onValueChange?: (value: string) => void
  onKeyDown?: (event: KeyboardEvent) => void
  onClick?: (event: MouseEvent) => void
  onPaste?: ClipboardEventHandler<HTMLInputElement>
  role?: AriaRole
  /**
   * AutoFocus can cause accessibility issues when used, especially in forms.
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/autofocus#accessibility_concerns
   */
  autoFocus?: boolean
}

interface Padding {
  input?: PaddingInline | PaddingLeftRight
  adornment?: { inline: ThemeKeys<"space"> }
}

interface PaddingInline {
  inline: ThemeKeys<"space">
  left?: never
  right?: never
}

interface PaddingLeftRight {
  left?: ThemeKeys<"space">
  right?: ThemeKeys<"space">
  inline?: never
}

export const BaseInput = forwardRef<HTMLInputElement, BaseInputProps>(
  function BaseInput(props, ref) {
    const {
      startAdornment,
      endAdornment,
      padding,
      inputMode = "text",
      variant = "normal",
      cursor = "text",
      maxLength,
      textOverflow,
      sx,
      onValueChange,
      onKeyDown,
      type,
      disabled,
      onPaste,
      prefix,
      prefixTooltip,
      suffix,
      autoFocus,
      ...otherProps
    } = props

    const inputPadding: CSSProperties = {
      paddingInline: padding?.input?.inline,
      paddingLeft: padding?.input?.left,
      paddingRight: padding?.input?.right,
    }

    return (
      <div className={styles.root} style={sx}>
        {startAdornment && (
          <div
            className={clsx(styles.adornment, styles.start)}
            style={{ paddingLeft: padding?.adornment?.inline }}
          >
            {startAdornment}
          </div>
        )}
        <Prefix prefix={prefix} prefixTooltip={prefixTooltip} />
        <input
          ref={ref}
          {...otherProps}
          className={clsx(
            styles.input,
            styles[`variant-${variant}`],
            styles[`cursor-${cursor}`],
            textOverflow && styles[`textOverflow-${textOverflow}`],
          )}
          style={inputPadding}
          inputMode={inputMode}
          onChange={(evt) => onValueChange?.(evt.currentTarget.value)}
          onKeyDown={(event) => onKeyDown?.(event)}
          onPaste={onPaste}
          maxLength={maxLength}
          type={type}
          disabled={disabled}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
        />
        <Suffix suffix={suffix} />
        {endAdornment && (
          <div
            className={clsx(styles.adornment, styles.end)}
            style={{ paddingRight: padding?.adornment?.inline }}
          >
            {endAdornment}
          </div>
        )}
      </div>
    )
  },
)

interface PrefixProps {
  prefixTooltip?: string
  prefix?: ReactNode
}

function Prefix(props: PrefixProps) {
  const { prefix: prefixContent, prefixTooltip } = props

  if (!prefixContent) return null

  const prefix = <div className={styles.prefix}>{prefixContent}</div>

  if (!prefixTooltip) return prefix

  return <Tooltip content={prefixTooltip}>{prefix}</Tooltip>
}

interface SuffixProps {
  suffix?: ReactNode
}

function Suffix(props: SuffixProps) {
  const { suffix } = props

  if (!suffix) return null

  return <div className={styles.suffix}>{suffix}</div>
}
